/**
 * CostAgreementPanel — per-line Agree/Disagree voting on cost lines.
 *
 * Each project member can vote 👍 Agree / 👎 Disagree on:
 *  - the project total
 *  - each construction phase
 *  - each sub-phase
 * Shows agreement % and locks the line when 100% agreement is reached.
 */
import React, { useMemo, useState } from 'react';
import ConstructionAPI from './api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ThumbsUp, ThumbsDown, Lock, Vote, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import InfoTip from './InfoTip';

import { makeFmt } from './currency';

export default function CostAgreementPanel({ calculation, savedProjectId, members, agreements, currentUser, onChange }) {
  const fmt$ = useMemo(() => makeFmt(calculation), [calculation]);
  const [voting, setVoting] = useState(null);

  const lines = useMemo(() => {
    if (!calculation) return [];
    const out = [
      { key: 'total', label: 'Project total (final cost)', amount: calculation.final_cost_usd, level: 0 },
    ];
    (calculation.phases || []).forEach(p => {
      out.push({ key: `phase:${p.phase}`, label: p.phase_label, amount: p.cost_usd, level: 1 });
      (p.wbs || []).forEach(w => {
        out.push({ key: `subphase:${p.phase}:${w.key}`, label: `↳ ${w.label}`, amount: w.cost_usd, level: 2 });
      });
    });
    return out;
  }, [calculation]);

  // members count = explicit invitees + the owner
  const memberCount = Math.max(1, (members?.length || 0) + 1);

  // group agreements by line_key
  const byLine = useMemo(() => {
    const m = {};
    (agreements || []).forEach(a => {
      (m[a.line_key] = m[a.line_key] || []).push(a);
    });
    return m;
  }, [agreements]);

  const myVote = (lineKey) => (byLine[lineKey] || []).find(a => a.author === currentUser);

  const vote = async (line, decision) => {
    if (!savedProjectId) return;
    try {
      setVoting(line.key + ':' + decision);
      await ConstructionAPI.addCostAgreement(savedProjectId, {
        line_key: line.key,
        line_label: line.label,
        line_amount_usd: line.amount,
        decision,
      });
      toast.success(decision === 'agree' ? 'Agreed' : 'Disagreed recorded');
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Vote failed');
    } finally {
      setVoting(null);
    }
  };

  if (!calculation) {
    return null;
  }

  return (
    <Card data-testid="cost-agreement-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base inline-flex items-center gap-1.5">
          <Vote className="h-4 w-4 text-orange-600" />
          Cost agreement
          <InfoTip>Each team member can Agree or Disagree on the project total, every phase and every sub-phase. Lines reach Locked status when 100% of members have agreed.</InfoTip>
        </CardTitle>
        <p className="text-xs text-slate-500">{memberCount} team member(s) on this project. Vote on any line below.</p>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="text-left p-2 w-[55%]">Cost line</th>
                <th className="text-right p-2">Amount</th>
                <th className="text-center p-2 w-32">Agreement</th>
                <th className="text-center p-2 w-44">Your vote</th>
              </tr>
            </thead>
            <tbody>
              {lines.map(ln => {
                const votes = byLine[ln.key] || [];
                const agrees = votes.filter(v => v.decision === 'agree').length;
                const total = votes.length;
                const pct = memberCount > 0 ? Math.round((agrees / memberCount) * 100) : 0;
                const locked = pct >= 100;
                const mine = myVote(ln.key);
                return (
                  <tr key={ln.key} className="border-t hover:bg-slate-50" data-testid={`cost-line-${ln.key.replace(/:/g,'-')}`}>
                    <td className={`p-2 ${ln.level === 0 ? 'font-bold text-slate-900' : ln.level === 1 ? 'font-semibold text-slate-800' : 'text-slate-600 pl-6'}`}>
                      {ln.label}
                      {locked && <Badge className="ml-2 bg-emerald-600 text-white text-[10px]"><Lock className="h-3 w-3 mr-0.5 inline" />Locked</Badge>}
                    </td>
                    <td className={`text-right p-2 ${ln.level === 0 ? 'font-bold' : ''}`}>{fmt$(ln.amount)}</td>
                    <td className="p-2">
                      <div className="flex items-center justify-center gap-1.5">
                        <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div className={`h-full ${pct >= 75 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`} style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-slate-700 w-10 text-right">{pct}%</span>
                      </div>
                      <div className="text-[10px] text-slate-400 text-center mt-0.5">{agrees}/{memberCount} agree · {total - agrees} dissent</div>
                    </td>
                    <td className="p-1">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          size="sm"
                          variant={mine?.decision === 'agree' ? 'default' : 'outline'}
                          className={`h-7 px-2 ${mine?.decision === 'agree' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                          onClick={() => vote(ln, 'agree')}
                          disabled={!savedProjectId || voting === ln.key + ':agree'}
                          data-testid={`vote-agree-${ln.key.replace(/:/g,'-')}`}
                        >
                          {voting === ln.key + ':agree' ? <Loader2 className="h-3 w-3 animate-spin" /> : <ThumbsUp className="h-3 w-3" />}
                        </Button>
                        <Button
                          size="sm"
                          variant={mine?.decision === 'disagree' ? 'default' : 'outline'}
                          className={`h-7 px-2 ${mine?.decision === 'disagree' ? 'bg-rose-600 hover:bg-rose-700' : ''}`}
                          onClick={() => vote(ln, 'disagree')}
                          disabled={!savedProjectId || voting === ln.key + ':disagree'}
                          data-testid={`vote-disagree-${ln.key.replace(/:/g,'-')}`}
                        >
                          {voting === ln.key + ':disagree' ? <Loader2 className="h-3 w-3 animate-spin" /> : <ThumbsDown className="h-3 w-3" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!savedProjectId && <p className="px-3 py-2 text-xs text-slate-500">Save the project on the Calculator tab to enable voting.</p>}
      </CardContent>
    </Card>
  );
}
